import type { Node } from "@xyflow/react";
import { isHTML, removeOptionalBrTags, hasBrTags } from "../utils/DomHandler";
import { NodeData } from "../types/types";

const collapseWhitespace = (value: string) => value.replace(/\s+/g, " ");

const normalizeMarkdown = (value: string) =>
  value
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

function htmlToMarkdown(html: string): string {
  if (typeof DOMParser === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const renderChildren = (node: ParentNode, depth = 0): string =>
    Array.from(node.childNodes)
      .map((child) => renderNode(child, depth))
      .join("");

  const renderListItem = (
    element: HTMLElement,
    depth: number,
    ordered: boolean,
  ) => {
    const indent = "  ".repeat(depth);
    const index =
      ordered && element.parentElement
        ? Array.from(element.parentElement.children).indexOf(element) + 1
        : 0;
    const marker = ordered ? `${index}. ` : "- ";
    const content = normalizeMarkdown(renderChildren(element, depth + 1));

    if (!content) return "";

    const lines = content.split("\n");
    return lines
      .map((line, lineIndex) =>
        lineIndex === 0
          ? `${indent}${marker}${line}`
          : `${indent}${" ".repeat(marker.length)}${line}`,
      )
      .join("\n");
  };

  const renderNode = (node: ChildNode, depth = 0): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return collapseWhitespace(node.textContent || "");
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const content = renderChildren(element, depth);

    switch (tag) {
      case "br":
        return "\n";
      case "p":
      case "div":
        return `${normalizeMarkdown(content)}\n\n`;
      case "strong":
      case "b":
        return `**${normalizeMarkdown(content)}**`;
      case "em":
      case "i":
        return `*${normalizeMarkdown(content)}*`;
      case "u":
        return content;
      case "code":
        return `\`${normalizeMarkdown(content)}\``;
      case "pre":
        return `\n\`\`\`\n${element.textContent || ""}\n\`\`\`\n\n`;
      case "a": {
        const href = element.getAttribute("href");
        const label = normalizeMarkdown(content) || href || "";
        return href ? `[${label}](${href})` : label;
      }
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6": {
        const level = Number(tag.slice(1));
        return `${"#".repeat(level)} ${normalizeMarkdown(content)}\n\n`;
      }
      case "ul":
        return `${Array.from(element.children)
          .map((child) => renderListItem(child as HTMLElement, depth, false))
          .filter(Boolean)
          .join("\n")}\n\n`;
      case "ol":
        return `${Array.from(element.children)
          .map((child) => renderListItem(child as HTMLElement, depth, true))
          .filter(Boolean)
          .join("\n")}\n\n`;
      case "li":
        return renderListItem(
          element,
          depth,
          element.parentElement?.tagName.toLowerCase() === "ol",
        );
      case "blockquote":
        return `${normalizeMarkdown(content)
          .split("\n")
          .map((line) => (line ? `> ${line}` : ">"))
          .join("\n")}\n\n`;
      default:
        return content;
    }
  };

  return normalizeMarkdown(renderChildren(doc.body));
}

export const getChildNodePosition = (
  event: React.MouseEvent<Element, MouseEvent>,
  parentNode?: Node,
  store?: any,
  project?: any,
) => {
  const { domNode } = store.getState();

  const positionAbsolute = (parentNode as any)?.internals?.positionAbsolute;
  const parentWidth =
    (parentNode as any)?.measured?.width ?? (parentNode as any)?.width;
  const parentHeight =
    (parentNode as any)?.measured?.height ?? (parentNode as any)?.height;

  if (!domNode || !positionAbsolute || !parentWidth || !parentHeight) {
    // console.log("parentNode not yet initialized", domNode);
    return;
  }

  const { top, left, width, height } = domNode.getBoundingClientRect();

  const paneCenterX = width / 2;
  const paneCenterY = height / 2;

  const panePosition = project({
    x: event.clientX - left - paneCenterX,
    y: event.clientY - top - paneCenterY,
  });

  return {
    x: panePosition.x - positionAbsolute.x + parentWidth / 2,
    y: panePosition.y - positionAbsolute.y + parentHeight / 2,
  };
};

export const selectedOrRootNode = (
  nodes: Node<NodeData>[],
  selectedNodeId: string | null,
) => {
  let node;
  // console.log("selectedNodeId", selectedNodeId);
  node = nodes.find((node) => node.id === selectedNodeId);
  // console.info("selectedChildNode", node);
  if (!node) {
    node = nodes.find((node) => node.id === "root" || node.type === "center");
    // console.info("selectedRootNode", node);
  }

  return node;
};

/**
 * Finds a non-overlapping position for a new node using spiral search.
 * @param nodes Existing nodes (with position, width, height)
 * @param getViewport Function to get current viewport (must return {x, y, zoom})
 * @param flowMapWrapper Ref to the flow map wrapper (for offsetWidth/Height)
 * @param selectedNode Optional selected node (to make the new node a child)
 * @param ideaWidth Width of the new node (default 50)
 * @param ideaHeight Height of the new node (default 18)
 * @returns { x: number, y: number, parentId?: string }
 */
export function getNonOverlappingPosition({
  nodes,
  viewport,
  flowMapWrapper,
  selectedNode,
  ideaWidth = 50,
  ideaHeight = 18,
}: {
  nodes: Array<{
    position: { x: number; y: number };
    width?: number;
    height?: number;
  }>;
  viewport: any;
  flowMapWrapper: React.RefObject<HTMLDivElement>;
  selectedNode?: { id: string };
  ideaWidth?: number;
  ideaHeight?: number;
}): { x: number; y: number; parentId?: string } {
  // Get all node positions and sizes
  const nodeRects = nodes.map((n) => ({
    x: n.position.x,
    y: n.position.y,
    width: n.width ?? 50,
    height: n.height ?? 18,
  }));

  // Get viewport bounds
  const paneWidth = 800; // fallback if not available
  const paneHeight = 600;
  const minX = -viewport.x / viewport.zoom;
  const minY = -viewport.y / viewport.zoom;
  const maxX =
    minX + (flowMapWrapper.current?.offsetWidth || paneWidth) / viewport.zoom;
  const maxY =
    minY + (flowMapWrapper.current?.offsetHeight || paneHeight) / viewport.zoom;

  // Try to find a free spot using a spiral search
  let found = false;
  let angle = 0;
  let radius = 160; // Increased from 0 to 60 for extra spacing
  let tryCount = 0;
  let newFocusX = minX + (maxX - minX) / 2;
  let newFocusY = minY + (maxY - minY) / 2;
  let parentId: string | undefined = undefined;
  if (selectedNode) {
    parentId = selectedNode.id;
  }
  const maxTries = 100;

  while (!found && tryCount < maxTries) {
    // Spiral out from center
    newFocusX = minX + (maxX - minX) / 2 + radius * Math.cos(angle);
    newFocusY = minY + (maxY - minY) / 2 + radius * Math.sin(angle);

    // Check for overlap
    const overlaps = nodeRects.some(
      (rect) =>
        newFocusX < rect.x + rect.width &&
        newFocusX + ideaWidth > rect.x &&
        newFocusY < rect.y + rect.height &&
        newFocusY + ideaHeight > rect.y,
    );

    if (
      !overlaps &&
      newFocusX > minX &&
      newFocusY > minY &&
      newFocusX + ideaWidth < maxX &&
      newFocusY + ideaHeight < maxY
    ) {
      found = true;
      break;
    }

    angle += Math.PI / 6;
    if (angle > 2 * Math.PI) {
      angle = 0;
      radius += 40;
    }
    tryCount++;
  }

  return { x: newFocusX, y: newFocusY, parentId };
}

export const convertHtmlToMarkdown = (inputHtml: string): string => {
  if (!isHTML(inputHtml)) {
    return inputHtml; // Return as is if not valid HTML
  }

  // Remove <br> tags if they are optional
  if (hasBrTags(inputHtml)) {
    inputHtml = removeOptionalBrTags(inputHtml);
  }
  return htmlToMarkdown(inputHtml);
};

export const splitTextIntoTitleAndNotes = (
  inputText: string,
  inputHtml: string,
): { title: string; notes: string } => {
  const titleLine = inputText.replace(/^\n+/, "").split("\n")[0].trim();
  const maxTitleLength = 50;

  // let markdown = markdownConverter.convert(inputHtml);
  // markdown = removeOptionalBrTags(markdown);

  let processedTitle = titleLine;
  if (processedTitle.length > maxTitleLength) {
    const lastSpace = processedTitle.lastIndexOf(" ", maxTitleLength);
    processedTitle =
      processedTitle.slice(0, lastSpace > 0 ? lastSpace : maxTitleLength) + "…";
  }

  //const notes = markdown.trim();

  return { title: processedTitle, notes: inputHtml };
};
